import Settings from './Settings';
import Grid from '../gui/Grid';
import Storage from './Storage';
import View from '../gui/View';
import Spinner from '../gui/Spinner';
import Selection from '../shared/Selection';
import ZoomMenu from '../gui/ZoomMenu';
import Menu from '../gui/Menu';
import ToolCollection from '../tools/edit/ToolCollection';
import SideBar from '../sidebar/edit/SideBar';
import ElementCollection from '../shapes/ElementCollection';
import DataHandler from './DataHandler';
import Cursor from '../gui/Cursor';
import PlantRow from '../sidebar/edit/PlantRow';
import Modal from '../gui/Modal';
import HoverSet from '../shared/HoverSet';
import SpeciesCollection from '../shared/SpeciesCollection';
import SpeciesInfo from '../sidebar/SpeciesInfo';
import ShapeFactory from '../shapes/ShapeFactory';
import DrawMenu from '../drawing/DrawMenu';
import TipBar from './TipBar';
import ImageMenu from '../gui/ImageMenu';
import ActionHandler from '../tools/actions/ActionHandler';
import DeviceDetector from "device-detector-js";
import MobileMenu from '../gui/MobileMenu';
import EventTypes from '../shared/EventTypes';
import OverviewCapture from '../gui/OverviewCapture';
import LayerCollection from '../shared/LayerCollection';
import ActiveContextPanel from '../drawing/ActiveContextPanel';
import Timeline from './Timeline';

export default class ServiceProvider {
    listeners = {};

    constructor(p) {
        this.p = p;
    }

    init() {
        this.initValues();
        this.initInterface();
    }

    initValues() {
        this.mode = 'edit';
        this.deviceDetector = new DeviceDetector();
        this.device = this.deviceDetector.parse(navigator.userAgent);
        this.mobile = this.device.device.type === 'smartphone';
        this.touch = ['tablet', 'smartphone'].includes(this.device.device.type);
        this.planName = null;
        this.backgroundColor = this.p.color(240);
        this.enableHotKeys = true;
        this.spinner = new Spinner(this);
        this.selection = new Selection(this);
        this.hoverSet = new HoverSet(this);
        this.unsavedChanges = false;
        this.cursor = new Cursor(this);
        this.elementFactory = new ShapeFactory(this);
        this.actions = new ActionHandler(this);
        this.image = null;
        this.loaded = false;
        this.debug = {};
        this.mouseX = 0;
        this.mouseY = 0;
    }

    initInterface() {
        this.storage = new Storage(this);
        this.dataHandler = new DataHandler(this);

        this.settings = new Settings(this);
        this.sideBar = new SideBar(this);
        this.sideBar.init();

        this.speciesInfo = new SpeciesInfo(this);

        this.zoomMenu = new ZoomMenu(this);
        this.zoomMenu.init();

        this.menu = this.mobile ? new MobileMenu(this) : new Menu(this);
        this.menu.init(
            ['select', 'shapes', 'navigation', 'erase', 'image'],
            ['settings', 'help', 'reset', 'save', 'undo', 'redo'],
        );

        if (this.dataHandler.isNewPlan()) {
            window['saveButton']?.hide();
        }

        this.drawMenu = new DrawMenu(this);
        this.activeContextPanel = new ActiveContextPanel(this);
        this.imageMenu = new ImageMenu(this);

        this.tools = new ToolCollection(this);
        this.tools.init();

        this.grid = new Grid(this);

        this.view = new View(this);
        this.grid.recalculate();

        this.speciesCollection = new SpeciesCollection(this);
        this.elements = new ElementCollection(this);
        this.layers = new LayerCollection(this);

        this.sideBar.activate();

        this.tips = new TipBar(this);
        this.timeline = new Timeline(this);
        this.overviewCapture = new OverviewCapture(this);

        this.dataHandler.getData();

        this.emit(EventTypes.UPDATE_UNIT_SYSTEM, this.settings.metric);
    }

    changesMade(silent = false) {
        if (!silent) {
            clearTimeout(this.saving);
            this.saving = setTimeout(() => this.storage.save(), 200);

            if (!this.dataHandler.isNewPlan() && this.settings.autoSave) {
                clearTimeout(this.autoSaving);
                this.autoSaving = setTimeout(() => this.dataHandler.savePlan(), 2000);
            }

            if (this.dataHandler.isNewPlan()) {
                this._checkNewPlanPrompts();
            }

            this.overviewCapture.schedule();
        }
        if (!this.unsavedChanges) {
            this.unsavedChanges = true;
            window['saveButton']?.style('color', '#ff0000');
        }
    }

    _checkNewPlanPrompts(showPrompt = true) {
        if (!this.loaded) return;
        let count = this.elements.collection.length;
        if (count > 0 && !this._newPlanSaveShown) {
            this._newPlanSaveShown = true;
            window['saveButton']?.show();
            if (this.unsavedChanges) {
                window['saveButton']?.style('color', '#ff0000');
            }
            this.menu.showNewPlanSaveButton?.();
        }
        if (showPrompt && count >= 10 && !this._nameModalDismissed && !this._nameModalShown) {
            this._nameModalShown = true;
            setTimeout(() => {
                this.dataHandler.askToSavePrompt(() => {
                    this._nameModalDismissed = true;
                });
            }, 700);
        }
    }

    changesSaved() {
        this.unsavedChanges = false;
        window['saveButton']?.html('Save');
        window['saveButton']?.style('color', '#000000');
    }

    saveFailed() {
        // Restore the button from its "..." in-progress label and keep it flagged
        // as unsaved (red) so the failed changes are clearly not persisted.
        this.unsavedChanges = true;
        window['saveButton']?.html('Save');
        window['saveButton']?.style('color', '#ff0000');
    }

    savePlan() {
        this.dataHandler.savePlan();
    }

    newPlantRow(species, container) {
        return new PlantRow(this, species, container);
    }

    getTab(tab) {
        return this.sideBar.tabs[tab];
    }

    emit(event, ...data) {
        this.listeners[event]?.forEach(listener => listener(...data));
    }

    on(event, listener) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(listener);
    }

    showWelcomeMessage() {
        let welcomeMessage = new Modal(this);
        welcomeMessage
            .onClickOutside(() => welcomeMessage.remove())
            .setTitle('Welcome')
            .addContent(this.p.createP(
                "Welcome to the garden planner! This is an early version, so please be patient with us as we work out the kinks and expand features."
            ))
            .addContent(this.p.createP(
                "To add plants, use the sidebar on the right to search the database for plants or select them from your lists. There, you will also find details about the plants and an overview of all plants in your garden plan."
            ))
            .addContent(this.p.createP(
                "You can also draw shapes and you can add plants to those! Multiple! This allows you to draw garden beds, for example, without having to place each individual plant."
            ))
            .addContent(this.p.createP(
                "Use the menu in the topleft to add shapes, navigate, erase, change settings or find all the hotkeys available to you. You can also add a background image, which could be a satellite image of your garden, for example."
            ))
            .addContent(this.p.createP(
                "If you have any questions or any feedback at all, please <a href='mailto:hello@permapeople.org' target='_blank'>email us</a> or join us on <a href='https://discord.gg/t6PP5BsJR2' target='_blank'>discord</a>."
            ))
            .setButtonText('Get started!')
            .onClickButton(() => welcomeMessage.remove());
    }

    showMigrationMessage() {
        let migrationMessage = new Modal(this);
        migrationMessage
            .onClickOutside(() => migrationMessage.remove())
            .setTitle('Welcome spring, welcome new features!')
            .addContent(this.p.createP(
                'It\'s been over a year since the last big update, and this one is huge!'
            ))
            .addContent(this.p.createP(
                "We've now given you a lot more power to make plans for your garden, we'll hope you'll love it. The first is a permaculture classic: layers!. We've prepopulated your plan with the 7 basic layers, but rename/remove them as you like. And add the ones you need. Planting plans will automatically assign the layer if the plant has it."
            ))
            .addContent(this.p.createP(
                "Another big one is the timeline. You can now give all elements added and removed dates. You can be as precise as needed. And then you can look back at how your garden has developed or look forward and plan all the things you're gonna grow."
            ))
            .addContent(this.p.createP(
                "And lastly we've added some new element options to make your plans more true and pretty. So check out the different segment types for free shapes and line, and the border on ellipses. And you can now give lines width, for example when drawing paths or streams. And as always, if you have any questions or any feedback at all, please <a href='mailto:hello@permapeople.org' target='_blank'>email us</a> or join us on <a href='https://discord.gg/t6PP5BsJR2' target='_blank'>discord</a>."
            ))
            .setButtonText('Explore what\'s new!')
            .onClickButton(() => migrationMessage.remove());
    }
}
